import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Phone,
  Mail,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  MessageSquare,
  Mic,
  MicOff,
  Plus,
  Edit2,
  Trash2,
  User,
  Building2,
  Shield,
  Upload,
  ChevronDown,
  ChevronUp,
  Loader2,
  ListTodo,
  ClipboardList,
  X,
  Send
} from "lucide-react";
import { authGet, authPost, authPut, authDelete, isAdvisor, getUserRole } from "../utils/auth";
import { ClaimHelpCard } from "../components/leads/ClaimHelpCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lead statuses
const LEAD_STATUSES = [
  { value: "new", label: "New", color: "slate" },
  { value: "meeting_done", label: "Meeting Done", color: "blue" },
  { value: "quote_sent", label: "Quote Sent", color: "purple" },
  { value: "proposal", label: "Proposal", color: "amber" },
  { value: "policy_issued", label: "Policy Issued", color: "green" },
  { value: "closed_won", label: "Closed Won", color: "emerald" },
  { value: "closed_lost", label: "Closed Lost", color: "red" }
];

// Task types
const TASK_TYPES = [
  { value: "pre_underwriting", label: "Pre-Underwriting Approval", color: "amber" },
  { value: "prepare_quote", label: "Prepare Quote", color: "blue" },
  { value: "document_collection", label: "Document Collection", color: "green" },
  { value: "follow_up", label: "Follow Up", color: "purple" }
];

// Activity type icons
const ACTIVITY_ICONS = {
  meeting: MessageSquare,
  task: ListTodo,
  note: FileText,
  document: Upload
};

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [riders, setRiders] = useState([]);
  
  // Voice-to-text
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef(null);
  
  // Modals
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEditLeadModal, setShowEditLeadModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  
  // Forms
  const [meetingForm, setMeetingForm] = useState({ summary: "", voice_transcript: "" });
  const [taskForm, setTaskForm] = useState({
    task_type: "",
    task_title: "",
    task_description: "",
    priority: "medium",
    companies: [],
    quote_requests: []
  });
  const [noteForm, setNoteForm] = useState({ note_text: "" });
  const [leadForm, setLeadForm] = useState({});
  const [responseForm, setResponseForm] = useState({ task_status: "", response_notes: "" });
  const [saving, setSaving] = useState(false);
  
  const userRole = getUserRole();
  const canManage = isAdvisor();

  useEffect(() => {
    fetchLeadDetail();
    fetchSettings();
    initializeSpeechRecognition();
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [leadId]);

  const initializeSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-IN';
      
      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          setTranscript(prev => prev + finalTranscript);
          setMeetingForm(prev => ({
            ...prev,
            voice_transcript: prev.voice_transcript + finalTranscript
          }));
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        if (event.error === 'not-allowed') {
          toast.error("Microphone access denied. Please allow microphone permissions.");
        }
      };
      
      recognition.onend = () => {
        setIsRecording(false);
      };
      
      recognitionRef.current = recognition;
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error("Speech recognition not supported in this browser");
      return;
    }
    
    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
      toast.info("Recording started. Speak clearly...");
    }
  };

  const fetchLeadDetail = async () => {
    try {
      const res = await authGet(`/api/advisor/leads/${leadId}`);
      if (res.ok) {
        setLead(res.data.lead);
        setLeadForm({
          name: res.data.lead.name || "",
          phone: res.data.lead.phone || "",
          email: res.data.lead.email || "",
          source: res.data.lead.source || "direct",
          status: res.data.lead.status || "new",
          notes: res.data.lead.notes || ""
        });
      } else {
        toast.error("Lead not found");
        navigate("/advisor/leads");
      }
    } catch (error) {
      console.error("Error fetching lead:", error);
      toast.error("Failed to fetch lead details");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const [companiesRes, ridersRes] = await Promise.all([
        authGet('/api/advisor/settings/companies'),
        authGet('/api/advisor/settings/riders')
      ]);
      
      if (companiesRes.ok) setCompanies(companiesRes.data.companies || []);
      if (ridersRes.ok) setRiders(ridersRes.data.riders || []);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleUpdateLead = async () => {
    setSaving(true);
    try {
      const res = await authPut(`/api/advisor/leads/${leadId}`, leadForm);
      if (res.ok) {
        toast.success("Lead updated!");
        setShowEditLeadModal(false);
        fetchLeadDetail();
      } else {
        throw new Error(res.data?.detail || "Failed to update lead");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMeeting = async () => {
    if (!meetingForm.summary && !meetingForm.voice_transcript) {
      toast.error("Please add a meeting summary or voice recording");
      return;
    }
    
    setSaving(true);
    try {
      const res = await authPost(`/api/advisor/leads/${leadId}/activities`, {
        activity_type: "meeting",
        summary: meetingForm.summary,
        voice_transcript: meetingForm.voice_transcript
      });
      
      if (res.ok) {
        toast.success("Meeting note added!");
        setShowMeetingModal(false);
        setMeetingForm({ summary: "", voice_transcript: "" });
        setTranscript("");
        fetchLeadDetail();
      } else {
        throw new Error(res.data?.detail || "Failed to add meeting");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTask = async () => {
    if (!taskForm.task_type) {
      toast.error("Please select a task type");
      return;
    }
    
    setSaving(true);
    try {
      const res = await authPost(`/api/advisor/leads/${leadId}/activities`, {
        activity_type: "task",
        task_type: taskForm.task_type,
        task_title: taskForm.task_title,
        task_description: taskForm.task_description,
        priority: taskForm.priority,
        companies: taskForm.companies,
        quote_requests: taskForm.quote_requests
      });
      
      if (res.ok) {
        toast.success("Task assigned!");
        setShowTaskModal(false);
        setTaskForm({
          task_type: "",
          task_title: "",
          task_description: "",
          priority: "medium",
          companies: [],
          quote_requests: []
        });
        fetchLeadDetail();
      } else {
        throw new Error(res.data?.detail || "Failed to assign task");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteForm.note_text) {
      toast.error("Please add a note");
      return;
    }
    
    setSaving(true);
    try {
      const res = await authPost(`/api/advisor/leads/${leadId}/activities`, {
        activity_type: "note",
        note_text: noteForm.note_text
      });
      
      if (res.ok) {
        toast.success("Note added!");
        setShowNoteModal(false);
        setNoteForm({ note_text: "" });
        fetchLeadDetail();
      } else {
        throw new Error(res.data?.detail || "Failed to add note");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateActivity = async () => {
    if (!selectedActivity) return;
    
    setSaving(true);
    try {
      const res = await authPut(
        `/api/advisor/leads/${leadId}/activities/${selectedActivity.id}`,
        responseForm
      );
      
      if (res.ok) {
        toast.success("Task updated!");
        setShowResponseModal(false);
        setSelectedActivity(null);
        setResponseForm({ task_status: "", response_notes: "" });
        fetchLeadDetail();
      } else {
        throw new Error(res.data?.detail || "Failed to update task");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!confirm("Are you sure you want to delete this activity?")) return;
    
    try {
      const res = await authDelete(`/api/advisor/leads/${leadId}/activities/${activityId}`);
      if (res.ok) {
        toast.success("Activity deleted!");
        fetchLeadDetail();
      } else {
        throw new Error(res.data?.detail || "Failed to delete");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openResponseModal = (activity) => {
    setSelectedActivity(activity);
    setResponseForm({
      task_status: activity.task_status || "pending",
      response_notes: activity.response_notes || ""
    });
    setShowResponseModal(true);
  };

  const getStatusInfo = (status) => {
    return LEAD_STATUSES.find(s => s.value === status) || LEAD_STATUSES[0];
  };

  const getTaskTypeInfo = (type) => {
    return TASK_TYPES.find(t => t.value === type) || TASK_TYPES[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleCompanySelection = (companyId) => {
    setTaskForm(prev => ({
      ...prev,
      companies: prev.companies.includes(companyId)
        ? prev.companies.filter(c => c !== companyId)
        : [...prev.companies, companyId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#A87608]" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8] flex items-center justify-center">
        <p className="text-[#3A4557]">Lead not found</p>
      </div>
    );
  }

  const statusInfo = getStatusInfo(lead.status);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#C9D4E6] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/advisor/leads")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>
            
            {canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEditLeadModal(true)}
                className="border-[#C9D4E6] text-[#3A4557]"
                data-testid="edit-lead-button"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Lead
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Lead Info Card */}
        <Card className="bg-white/50 border-[#C9D4E6] mb-6" data-testid="lead-info-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center">
                  <span className="text-[#2E3B57] font-bold text-2xl">
                    {lead.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-[#1B2A4A]">{lead.name}</h1>
                  <div className="flex items-center gap-4 mt-2 text-[#3A4557]">
                    {lead.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {lead.phone}
                      </span>
                    )}
                    {lead.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-4 w-4" />
                        {lead.email}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <span className={`text-sm px-3 py-1 rounded-full bg-${statusInfo.color}-500/20 text-${statusInfo.color}-700`}>
                  {statusInfo.label}
                </span>
                <p className="text-[#3A4557] text-xs mt-2">
                  Source: {lead.source === 'claim_help' ? 'Claim Help (Website)' : (lead.source || 'Direct')}
                </p>
                <p className="text-[#3A4557] text-xs">
                  Created: {formatDate(lead.created_at)}
                </p>
              </div>
            </div>
            
            {lead.notes && (
              <div className="mt-4 p-3 bg-[#EDF1F8]/50 rounded-lg">
                <p className="text-[#3A4557] text-sm">{lead.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {lead.claim_help && <ClaimHelpCard lead={lead} />}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          {canManage && (
            <>
              <Button
                onClick={() => setShowMeetingModal(true)}
                className="bg-blue-500 hover:bg-[#EDF1F8] text-white"
                data-testid="add-meeting-button"
              >
                <Mic className="h-4 w-4 mr-2" />
                Add Meeting Note
              </Button>
              <Button
                onClick={() => setShowTaskModal(true)}
                className="bg-purple-500 hover:bg-purple-600 text-white"
                data-testid="add-task-button"
              >
                <ListTodo className="h-4 w-4 mr-2" />
                Assign Task
              </Button>
              <Button
                onClick={() => setShowNoteModal(true)}
                variant="outline"
                className="border-[#C9D4E6] text-[#2E3B57]"
                data-testid="add-note-button"
              >
                <FileText className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </>
          )}
        </div>

        {/* Activity Timeline */}
        <Card className="bg-white/50 border-[#C9D4E6]">
          <CardHeader>
            <CardTitle className="text-[#1B2A4A] flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#A87608]" />
              Activity Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!lead.activities || lead.activities.length === 0) ? (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                <p className="text-[#3A4557]">No activities yet</p>
                {canManage && (
                  <p className="text-[#3A4557] text-sm mt-2">
                    Add a meeting note or assign a task to get started
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {lead.activities.map((activity) => {
                  const Icon = ACTIVITY_ICONS[activity.activity_type] || FileText;
                  const isTask = activity.activity_type === 'task';
                  const taskTypeInfo = isTask ? getTaskTypeInfo(activity.task_type) : null;
                  
                  return (
                    <div
                      key={activity.id}
                      className="relative pl-8 pb-4 border-l-2 border-[#C9D4E6] last:border-l-0"
                      data-testid={`activity-${activity.id}`}
                    >
                      {/* Timeline dot */}
                      <div className={`absolute -left-[9px] top-0 h-4 w-4 rounded-full ${
                        isTask 
                          ? activity.task_status === 'completed' 
                            ? 'bg-green-500' 
                            : activity.task_status === 'in_progress'
                              ? 'bg-blue-500'
                              : 'bg-[#D4A017]'
                          : 'bg-[#E2E9F4]'
                      }`} />
                      
                      {/* Activity Card */}
                      <div className={`bg-[#EDF1F8]/50 rounded-lg p-4 ml-4 ${
                        isTask && activity.task_status === 'pending' ? 'border border-[#D4A017]/30' : ''
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${
                              isTask ? `text-${taskTypeInfo?.color}-700` : 'text-[#3A4557]'
                            }`} />
                            <span className="text-[#1B2A4A] font-medium">
                              {activity.activity_type === 'meeting' && 'Meeting Note'}
                              {activity.activity_type === 'task' && (activity.task_title || taskTypeInfo?.label)}
                              {activity.activity_type === 'note' && 'Note'}
                              {activity.activity_type === 'document' && 'Document Uploaded'}
                            </span>
                            
                            {isTask && (
                              <span className={`text-xs px-2 py-0.5 rounded-full bg-${
                                activity.task_status === 'completed' ? 'green' :
                                activity.task_status === 'in_progress' ? 'blue' : 'amber'
                              }-500/20 text-${
                                activity.task_status === 'completed' ? 'green' :
                                activity.task_status === 'in_progress' ? 'blue' : 'amber'
                              }-400`}>
                                {activity.task_status === 'completed' ? 'Completed' :
                                 activity.task_status === 'in_progress' ? 'In Progress' : 'Pending'}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-[#3A4557] text-xs">
                              {formatDate(activity.created_at)}
                            </span>
                            {isTask && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openResponseModal(activity)}
                                className="h-7 text-[#A87608] hover:text-[#A87608]"
                                data-testid={`update-task-${activity.id}`}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                            )}
                            {canManage && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteActivity(activity.id)}
                                className="h-7 text-red-600 hover:text-red-700"
                                data-testid={`delete-activity-${activity.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Activity Content */}
                        <div className="mt-3 text-[#2E3B57] text-sm">
                          {activity.activity_type === 'meeting' && (
                            <>
                              {activity.summary && <p>{activity.summary}</p>}
                              {activity.voice_transcript && (
                                <div className="mt-2 p-2 bg-white/50 rounded border border-[#C9D4E6]">
                                  <p className="text-[#3A4557] text-xs mb-1">Voice Transcript:</p>
                                  <p className="text-[#3A4557]">{activity.voice_transcript}</p>
                                </div>
                              )}
                            </>
                          )}
                          
                          {activity.activity_type === 'task' && (
                            <>
                              {activity.task_description && <p>{activity.task_description}</p>}
                              
                              {activity.companies && activity.companies.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-[#3A4557] text-xs">Companies:</p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {activity.companies.map((companyId, idx) => {
                                      const company = companies.find(c => c.id === companyId);
                                      return (
                                        <span key={idx} className="bg-[#EDF1F8]/40 text-[#2E3B57] text-xs px-2 py-0.5 rounded">
                                          {company?.name || companyId}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              
                              {activity.response_notes && (
                                <div className="mt-2 p-2 bg-green-900/20 rounded border border-green-700/30">
                                  <p className="text-green-600 text-xs mb-1">Employee Response:</p>
                                  <p className="text-[#2E3B57]">{activity.response_notes}</p>
                                </div>
                              )}
                            </>
                          )}
                          
                          {activity.activity_type === 'note' && (
                            <p>{activity.note_text}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit Lead Modal */}
      <Dialog open={showEditLeadModal} onOpenChange={setShowEditLeadModal}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#A87608]" />
              Edit Lead
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#3A4557] text-xs">Name</Label>
              <Input
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#3A4557] text-xs">Phone</Label>
                <Input
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                  className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                />
              </div>
              <div>
                <Label className="text-[#3A4557] text-xs">Email</Label>
                <Input
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#3A4557] text-xs">Status</Label>
              <Select value={leadForm.status} onValueChange={(v) => setLeadForm({ ...leadForm, status: v })}>
                <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  {LEAD_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-[#3A4557] text-xs">Notes</Label>
              <textarea
                value={leadForm.notes}
                onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })}
                rows={2}
                className="mt-1 w-full px-3 py-2 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm resize-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowEditLeadModal(false)}
                className="flex-1 border-[#C9D4E6]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateLead}
                disabled={saving}
                className="flex-1 bg-[#D4A017] hover:bg-[#D4A017] text-black"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Meeting Note Modal */}
      <Dialog open={showMeetingModal} onOpenChange={setShowMeetingModal}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <Mic className="h-5 w-5 text-[#2E3B57]" />
              Add Meeting Note
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Voice Recording */}
            <div className="p-4 bg-[#EDF1F8]/50 rounded-lg border border-[#C9D4E6]">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[#3A4557] text-sm">Voice-to-Text Recording</p>
                <Button
                  onClick={toggleRecording}
                  variant={isRecording ? "destructive" : "outline"}
                  size="sm"
                  className={isRecording ? "bg-red-500 hover:bg-red-600" : "border-[#C9D4E6]"}
                  data-testid="voice-record-button"
                >
                  {isRecording ? (
                    <>
                      <MicOff className="h-4 w-4 mr-2" />
                      Stop Recording
                    </>
                  ) : (
                    <>
                      <Mic className="h-4 w-4 mr-2" />
                      Start Recording
                    </>
                  )}
                </Button>
              </div>
              
              {isRecording && (
                <div className="flex items-center gap-2 text-red-600 text-sm mb-2">
                  <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
                  Recording in progress...
                </div>
              )}
              
              {meetingForm.voice_transcript && (
                <div className="p-3 bg-white/50 rounded text-[#2E3B57] text-sm max-h-32 overflow-y-auto">
                  {meetingForm.voice_transcript}
                </div>
              )}
              
              {!recognitionRef.current && (
                <p className="text-[#A87608] text-xs">
                  Voice recording not supported in this browser. Please use Chrome.
                </p>
              )}
            </div>
            
            {/* Manual Summary */}
            <div>
              <Label className="text-[#3A4557] text-xs">Meeting Summary (Manual)</Label>
              <textarea
                value={meetingForm.summary}
                onChange={(e) => setMeetingForm({ ...meetingForm, summary: e.target.value })}
                placeholder="Type your meeting notes here..."
                rows={4}
                className="mt-1 w-full px-3 py-2 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm resize-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowMeetingModal(false);
                  setMeetingForm({ summary: "", voice_transcript: "" });
                  setTranscript("");
                }}
                className="flex-1 border-[#C9D4E6]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddMeeting}
                disabled={saving}
                className="flex-1 bg-blue-500 hover:bg-[#EDF1F8]"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Meeting Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-purple-600" />
              Assign Task to Employee
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Task Type */}
            <div>
              <Label className="text-[#3A4557] text-xs">Task Type *</Label>
              <Select value={taskForm.task_type} onValueChange={(v) => setTaskForm({ ...taskForm, task_type: v })}>
                <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                  <SelectValue placeholder="Select task type..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  {TASK_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Task Title */}
            <div>
              <Label className="text-[#3A4557] text-xs">Task Title</Label>
              <Input
                value={taskForm.task_title}
                onChange={(e) => setTaskForm({ ...taskForm, task_title: e.target.value })}
                placeholder="Brief title..."
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
              />
            </div>
            
            {/* Task Description */}
            <div>
              <Label className="text-[#3A4557] text-xs">Instructions for Employee</Label>
              <textarea
                value={taskForm.task_description}
                onChange={(e) => setTaskForm({ ...taskForm, task_description: e.target.value })}
                placeholder="Detailed instructions..."
                rows={3}
                className="mt-1 w-full px-3 py-2 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm resize-none"
              />
            </div>
            
            {/* Companies Selection (for pre-underwriting and quote tasks) */}
            {(taskForm.task_type === 'pre_underwriting' || taskForm.task_type === 'prepare_quote') && (
              <div>
                <Label className="text-[#3A4557] text-xs">Select Companies</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {companies.map(company => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => toggleCompanySelection(company.id)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        taskForm.companies.includes(company.id)
                          ? 'bg-blue-500 text-white'
                          : 'bg-[#EDF1F8] text-[#2E3B57] hover:bg-[#E2E9F4]'
                      }`}
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Priority */}
            <div>
              <Label className="text-[#3A4557] text-xs">Priority</Label>
              <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowTaskModal(false);
                  setTaskForm({
                    task_type: "",
                    task_title: "",
                    task_description: "",
                    priority: "medium",
                    companies: [],
                    quote_requests: []
                  });
                }}
                className="flex-1 border-[#C9D4E6]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddTask}
                disabled={saving}
                className="flex-1 bg-purple-500 hover:bg-purple-600"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Assign Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#3A4557]" />
              Add Note
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#3A4557] text-xs">Note</Label>
              <textarea
                value={noteForm.note_text}
                onChange={(e) => setNoteForm({ ...noteForm, note_text: e.target.value })}
                placeholder="Enter your note..."
                rows={4}
                className="mt-1 w-full px-3 py-2 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm resize-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowNoteModal(false);
                  setNoteForm({ note_text: "" });
                }}
                className="flex-1 border-[#C9D4E6]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddNote}
                disabled={saving}
                className="flex-1 bg-[#E2E9F4] hover:bg-[#EDF1F8]"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Task Modal (for employee responses) */}
      <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Update Task Status
            </DialogTitle>
          </DialogHeader>

          {selectedActivity && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-[#EDF1F8]/50 rounded-lg">
                <p className="text-[#1B2A4A] font-medium">
                  {selectedActivity.task_title || getTaskTypeInfo(selectedActivity.task_type)?.label}
                </p>
                {selectedActivity.task_description && (
                  <p className="text-[#3A4557] text-sm mt-1">{selectedActivity.task_description}</p>
                )}
              </div>
              
              <div>
                <Label className="text-[#3A4557] text-xs">Status</Label>
                <Select 
                  value={responseForm.task_status} 
                  onValueChange={(v) => setResponseForm({ ...responseForm, task_status: v })}
                >
                  <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-[#3A4557] text-xs">Response Notes</Label>
                <textarea
                  value={responseForm.response_notes}
                  onChange={(e) => setResponseForm({ ...responseForm, response_notes: e.target.value })}
                  placeholder="Add your response or completion notes..."
                  rows={3}
                  className="mt-1 w-full px-3 py-2 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedActivity(null);
                  }}
                  className="flex-1 border-[#C9D4E6]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateActivity}
                  disabled={saving}
                  className="flex-1 bg-green-500 hover:bg-green-600"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Update Task
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
